/**
 * Given Canvas App origin, returns corresponding Designer App origin
 */
export const getDesignerAppOrigin = (canvasAppHref: string) => {
  const canvasAppOrigin = new URL(canvasAppHref).origin;
  const canvasAppOriginPattern = /^https:\/\/.+\.canvas\.[0-9a-zA-Z\-]+\..+/;

  // Replace '.canvas.' with '.design.' only if the pattern matches
  if (canvasAppOriginPattern.test(canvasAppOrigin)) {
    return canvasAppOrigin.replace('.canvas.', '.design.');
  }

  throw new Error('Invalid Canvas App origin');
};

/**
 * Given Canvas app origin, returns Designer Preview origin
 */
export const getDesignerPreviewOrigin = (canvasAppHref: string) => {
  const canvasAppOrigin = new URL(canvasAppHref).origin;
  const canvasAppOriginPattern = /^https:\/\/.+\.canvas\.[0-9a-zA-Z\-]+\..+/;

  // Replace '{siteSlug}.canvas.' with 'preview.' only if the pattern matches
  if (canvasAppOriginPattern.test(canvasAppOrigin)) {
    return `https://preview.${canvasAppOrigin.split('.canvas.')[1]}`;
  }

  throw new Error('Invalid Canvas App origin');
};

export const getAnonymousCommentsAppOrigin = (canvasAppHref: string) => {
  const canvasAppOrigin = new URL(canvasAppHref).origin;
  const canvasAppOriginPattern = /^https:\/\/.+\.canvas\.[0-9a-zA-Z\-]+\..+/;

  // Replace '{siteSlug}.canvas.' with 'comments.' only if the pattern matches
  if (canvasAppOriginPattern.test(canvasAppOrigin)) {
    return `https://comments.${canvasAppOrigin.split('.canvas.')[1]}`;
  }

  throw new Error('Invalid Canvas App origin');
};

/**
 * Given Canvas app window object, identifies parent window origin
 */
export const getCanvasAppParentOrigin = (canvasAppWindow: Window) => {
  const isDesignerReadOnly =
    canvasAppWindow.document.documentElement.classList.contains('wf-read-only');
  const isAnonymousCommentsApp =
    canvasAppWindow.document.documentElement.classList.contains(
      'wf-anonymous-comment'
    );

  if (isDesignerReadOnly) {
    return getDesignerPreviewOrigin(canvasAppWindow.location.href);
  } else if (isAnonymousCommentsApp) {
    return getAnonymousCommentsAppOrigin(canvasAppWindow.location.href);
  } else {
    return getDesignerAppOrigin(canvasAppWindow.location.href);
  }
};

/**
 * Given Designer App origin, returns corresponding Canvas App origin
 */
export const getCanvasAppOrigin = (windowHref: string) => {
  const url = new URL(windowHref);
  const designerAppOriginPattern = /^https:\/\/.+\.design\.[0-9a-zA-Z\-]+\..+/;
  const previewAppOriginPattern = /^https:\/\/preview\..+?\/preview\//;
  const commentsAppOriginPattern = /^https:\/\/comments\..+?\/comment\//;

  // Replace '.design.' or '.preview.' with '.canvas.' only if the pattern matches
  if (designerAppOriginPattern.test(url.origin)) {
    return url.origin.replace(/\.design\./, '.canvas.');
  } else if (previewAppOriginPattern.test(url.href)) {
    const siteId = url.pathname.split('/')[2];
    if (siteId) {
      return `https://${siteId}.canvas.${url.host.replace('preview.', '')}`;
    }
  } else if (commentsAppOriginPattern.test(url.href)) {
    const siteId = url.pathname.split('/')[2];
    if (siteId) {
      return `https://${siteId}.canvas.${url.host.replace('comments.', '')}`;
    }
  }

  throw new Error('Invalid Designer App origin');
};

export const isCanvasAppOrigin = (
  eventOrigin: string,
  currentOrigin: string
): boolean => {
  try {
    const canvasAppOrigin = getCanvasAppOrigin(currentOrigin);
    return eventOrigin === canvasAppOrigin;
  } catch (e) {
    return false;
  }
};
